/**
 * Per-project UI settings, persisted in localStorage (one entry per project).
 *
 * One JSON blob holds all remembered settings for a project (video playback
 * rate today, more to come), keyed by projectId. This is intentionally generic
 * so new preferences can be added as fields without introducing new storage
 * keys. localStorage is used — rather than a cookie — because these are purely
 * client-side preferences the API never needs, matching the rest of the app's
 * `dashboard.*` persisted state.
 *
 * Every read is defensive: a missing, malformed, or unreadable entry yields an
 * empty settings object so callers transparently fall back to their defaults.
 * Writes are best-effort and never throw.
 */

export interface ProjectSettings {
    videoPlaybackRate?: number;
}

const storageKey = (projectId: string) => `dashboard.projectSettings.${projectId}`;

export function getProjectSettings(projectId?: string): ProjectSettings {
    if (!projectId) return {};
    try {
        if (typeof localStorage === "undefined") return {};
        const raw = localStorage.getItem(storageKey(projectId));
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
            ? (parsed as ProjectSettings)
            : {};
    } catch {
        return {};
    }
}

export function setProjectSetting<K extends keyof ProjectSettings>(
    projectId: string | undefined,
    key: K,
    value: ProjectSettings[K],
): void {
    if (!projectId) return;
    try {
        if (typeof localStorage === "undefined") return;
        const next = { ...getProjectSettings(projectId), [key]: value };
        localStorage.setItem(storageKey(projectId), JSON.stringify(next));
    } catch {
        // Persisting a setting must never break the UI.
    }
}
